// Account detail (design system 06-modules §1.3 R4): the single home for per-account mutation.
// Whose account it is stays evident within the surface; rights conferred elsewhere are shown as
// status with a route to where they are conferred, and become editable only for a superuser;
// the acting administrator's own account offers no lock-out action and says so. Every operation
// is re-resolved server-side — a 403 renders as permission-denied, never as a silent no-op.
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { AdminGroupsResponse, AdminUserDetail } from '../../shared/api-types.js';
import { SUBJECT_PROVIDER_LABELS } from '../../shared/enums.js';
import { failureKind, queryString, type ApiFailure, type ApiResult } from '../api.js';
import { PATHS } from '../paths.js';
import { AdminSectionStrip } from '../shell/admin.js';
import { useMeContext } from '../shell/me-context.js';
import {
  Banner,
  Button,
  Checkbox,
  ConfirmDialog,
  FailureView,
  Fieldset,
  LinkButton,
  Page,
  PageHead,
  RelativeTime,
  Section,
  Skeleton,
  StateView,
  useRequest,
  useToast,
} from '../ui/index.js';
import { adminApi } from './admin-api.js';
import { HIGHLIGHT_LATEST, describeConfers, sectionsHeld, stateTitle } from './admin-helpers.js';
import { UnsavedChangesGuard } from '../ui/unsaved-changes.js';
import { AccountDeletionBody, ApproveDialog, OperationFailure, RightsChips, StatePill } from './parts.js';
import './admin.css';

type Confirmation = 'deny' | 'suspend' | 'revoke' | 'delete';

export default function AdminUserDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { me, reload: reloadMe } = useMeContext();
  const isSuperuser = me.subject.isSuperuser;
  const toast = useToast();
  const navigate = useNavigate();
  const detail = useRequest<AdminUserDetail>((signal) => adminApi.user(id, signal), [id]);
  const account = detail.data;

  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [approval, setApproval] = useState<'approve' | 'reinstate' | null>(null);
  const [approvalError, setApprovalError] = useState<ApiFailure | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [attempted, setAttempted] = useState('change this account');

  const auditLink = account ? `${PATHS.adminAudit}${queryString({ target: account.auditLink.target, targetType: 'subject' })}` : PATHS.adminAudit;

  const reportFailure = (result: ApiFailure, what: string): void => {
    setAttempted(what);
    setFailure(result);
    if (failureKind(result) === 'denied') {
      // Why: the server re-resolved our permission and said no; refresh what we hold and show.
      reloadMe();
      detail.reload();
    }
  };

  const run = async (what: string, call: () => Promise<ApiResult<AdminUserDetail>>, success: string): Promise<void> => {
    setBusy(true);
    setFailure(null);
    const result = await call();
    setBusy(false);
    setConfirmation(null);
    if (!result.ok) {
      reportFailure(result, what);
      return;
    }
    detail.setData(result.data);
    toast.success(success, me.admin.audit.view ? { action: { label: 'View audit trail', onClick: () => navigate(`${auditLink}&highlight=${HIGHLIGHT_LATEST}`) } } : undefined);
  };

  const confirmAction = async (): Promise<void> => {
    if (!account || !confirmation) return;
    const name = account.displayName;
    switch (confirmation) {
      case 'deny':
        await run(
          account.status === 'pending' ? `refuse ${name}'s request` : `deny ${name}`,
          () => adminApi.deny(account.id),
          account.status === 'pending' ? `Refused ${name}'s request.` : `Denied ${name}. They are signed out everywhere and hold no groups.`,
        );
        break;
      case 'suspend':
        await run(`suspend ${name}`, () => adminApi.suspend(account.id), `Suspended ${name}. They are signed out everywhere and hold no groups.`);
        break;
      case 'revoke':
        await run(`sign ${name} out everywhere`, () => adminApi.revokeSessions(account.id), `Signed ${name} out everywhere.`);
        break;
      case 'delete': {
        setBusy(true);
        setFailure(null);
        const result = await adminApi.deleteUser(account.id);
        setBusy(false);
        setConfirmation(null);
        if (!result.ok) {
          reportFailure(result, `delete ${name}'s account`);
          return;
        }
        toast.success(`Deleted ${name}'s account. Audit records remain.`);
        navigate(PATHS.adminUsers, { replace: true });
        break;
      }
      default:
        break;
    }
  };

  const approve = async (groupIds: string[]): Promise<void> => {
    if (!account || !approval) return;
    setBusy(true);
    setApprovalError(null);
    const result = await adminApi.approve(account.id, groupIds);
    setBusy(false);
    if (!result.ok) {
      if (failureKind(result) === 'denied') {
        setApproval(null);
        reportFailure(result, approval === 'reinstate' ? 'reinstate this account' : 'approve this account');
        return;
      }
      setApprovalError(result);
      return;
    }
    detail.setData(result.data);
    setApproval(null);
    toast.success(`${approval === 'reinstate' ? 'Reinstated' : 'Approved'} ${account.displayName}.`);
  };

  const title = account?.displayName ?? 'Account';
  const crumbs = [{ label: 'Users', to: PATHS.adminUsers, role: 'module' as const }];

  if (detail.status === 'error' && detail.error && !account) {
    return (
      <Page measure="reading">
        <PageHead title="Account" crumbs={crumbs} strip={<AdminSectionStrip />} />
        <FailureView failure={detail.error} what="this account" onRetry={detail.reload} backTo={{ to: PATHS.adminUsers, label: 'Back to users' }} minHeight="40dvh" />
      </Page>
    );
  }

  return (
    <Page measure="reading">
      <PageHead
        title={title}
        titleAside={account ? <StatePill status={account.status} /> : undefined}
        identifier={account ? <span className="selectable">{account.email}</span> : undefined}
        crumbs={crumbs}
        strip={<AdminSectionStrip />}
        actions={me.admin.audit.view ? <LinkButton to={auditLink} icon="list" variant="secondary">View audit trail</LinkButton> : undefined}
      />

      <OperationFailure failure={failure} attempted={attempted} onDismiss={() => setFailure(null)} />

      {account?.selfRow && isSuperuser ? (
        <Banner tone="info" title="Your own account">
          Lock-out actions are not offered on your own account. To end your session, use Sign out in the account menu.
        </Banner>
      ) : null}

      <Section id="account" title="Account">
        {account ? <AccountFacts account={account} /> : <AccountSkeleton />}
      </Section>

      <Section
        id="rights"
        title="Rights"
        description={isSuperuser
          ? account?.status === 'approved'
            ? 'Tick the groups this account belongs to; changes take effect on their next request.'
            : 'Groups are assigned when the account is approved or reinstated.'
          : undefined}
      >
        {account ? (
          <div className="stack">
            <RightsChips rights={account.rights} linkGroups={me.admin.groups} who={account.displayName} />
            <SectionsHeld rights={account.rights} />
            {isSuperuser ? (
              <MembershipEditor
                account={account}
                busy={busy}
                onSave={(groupIds) => run('change these groups', () => adminApi.setUserGroups(account.id, groupIds), `Updated ${account.displayName}'s groups.`)}
              />
            ) : null}
          </div>
        ) : <Skeleton width="50%" />}
      </Section>

      {isSuperuser && account && !account.selfRow ? (
        <Section
          id="actions"
          title="Actions"
          description={account.isSuperuser
            ? 'A superuser is managed by the deployment: denying, suspending or deleting it here would be undone at their next sign-in. To end it, remove the address from SUPERUSER_EMAILS and run superuser:revoke.'
            : undefined}
        >
          <AccountActions account={account} busy={busy} onApprove={(mode) => setApproval(mode)} onConfirm={setConfirmation} />
        </Section>
      ) : null}

      {!isSuperuser && account ? (
        <p className="secondary text-sm">Only a superuser can change this account.</p>
      ) : null}

      {account && approval ? (
        <ApproveDialog
          open
          accounts={[account]}
          mode={approval}
          onCancel={() => { setApproval(null); setApprovalError(null); }}
          onConfirm={approve}
          busy={busy}
          error={approvalError}
        />
      ) : null}

      {account ? (
        <>
          <ConfirmDialog open={confirmation === 'suspend'} title={`Suspend ${account.displayName}?`} confirmLabel="Suspend" danger busy={busy} onCancel={() => setConfirmation(null)} onConfirm={confirmAction}>
            <p>They are signed out everywhere and removed from every group. Reinstating later means choosing their groups again. Their meetings and to-dos are kept.</p>
          </ConfirmDialog>
          <ConfirmDialog
            open={confirmation === 'deny'}
            title={account.status === 'pending' ? `Refuse ${account.displayName}'s request?` : `Deny ${account.displayName} access?`}
            confirmLabel={account.status === 'pending' ? 'Refuse' : 'Deny access'}
            danger
            busy={busy}
            onCancel={() => setConfirmation(null)}
            onConfirm={confirmAction}
          >
            {account.status === 'pending'
              ? <p>They will not be able to sign in. Their request stays on file, and you can approve them later.</p>
              : <p>They are signed out everywhere, removed from every group, and cannot sign in until reinstated. Their meetings and to-dos are kept.</p>}
          </ConfirmDialog>
          <ConfirmDialog open={confirmation === 'revoke'} title={`Sign ${account.displayName} out everywhere?`} confirmLabel="Sign out everywhere" busy={busy} onCancel={() => setConfirmation(null)} onConfirm={confirmAction}>
            <p>Every active session ends now. Their groups are unchanged and they can sign in again straight away.</p>
          </ConfirmDialog>
          <ConfirmDialog open={confirmation === 'delete'} title={`Delete ${account.displayName}'s account?`} confirmLabel="Delete account" danger busy={busy} typeToConfirm={account.email} onCancel={() => setConfirmation(null)} onConfirm={confirmAction}>
            <AccountDeletionBody />
          </ConfirmDialog>
        </>
      ) : null}
    </Page>
  );
}

// --- Facts ---------------------------------------------------------------------------------------

function AccountFacts({ account }: { account: AdminUserDetail }) {
  return (
    <div className="sheet stack">
      <dl className="field-list">
        <dt>State</dt>
        <dd><StatePill status={account.status} /> <span className="text-sm muted">{stateTitle(account.status)}</span></dd>
        <dt>Last active</dt>
        <dd><RelativeTime value={account.lastSeenAt} mode="both" /></dd>
        <dt>Requested</dt>
        <dd><RelativeTime value={account.requestedAt} mode="both" /></dd>
        <dt>Decided</dt>
        <dd>
          {account.decidedAt ? <RelativeTime value={account.decidedAt} mode="both" /> : '—'}
          {account.decidedBy ? <span className="text-sm muted"> by {account.decidedBy.name}</span> : null}
        </dd>
        <dt>Signs in with</dt>
        <dd>{SUBJECT_PROVIDER_LABELS[account.provider]}{account.emailVerified ? '' : ' — email not verified'}</dd>
        <dt>Timezone</dt>
        <dd>{account.timezone ?? '—'}</dd>
        <dt>Created</dt>
        <dd><RelativeTime value={account.createdAt} mode="both" /></dd>
      </dl>
    </div>
  );
}

/**
 * The administrative sections the account holds, read-only, for every reader of the account (owner ruling O1 of
 * 2026-10-05; D8): before, a users:view delegate never saw them — they appeared only inside the superuser's group
 * checkboxes below. Status, not a control (06-modules §1.3 R4): they are changed by changing the account's groups.
 */
function SectionsHeld({ rights }: { rights: unknown }) {
  const held = sectionsHeld(rights);
  if (held.length === 0) return null;
  return (
    <div className="stack stack--2">
      <h3>Administrative sections</h3>
      <dl className="field-list">
        {held.map((entry) => (
          <Fragment key={entry.section}>
            <dt>{entry.name}</dt>
            <dd>
              {entry.reading} <span className="text-sm muted">{entry.description}</span>
            </dd>
          </Fragment>
        ))}
      </dl>
    </div>
  );
}

function AccountSkeleton() {
  return (
    <div className="sheet stack" aria-busy="true">
      <Skeleton width="70%" />
      <Skeleton width="60%" />
      <Skeleton width="65%" />
    </div>
  );
}

// --- Membership editor (superuser) ---------------------------------------------------------------

/**
 * The account's groups as a checkbox set with one Save for the set — one operation on the server
 * (PUT groups), audited once. Only an approved account can hold groups; for any other state the
 * approve/reinstate dialog is where groups are assigned — the Rights section's description says so,
 * and this editor renders nothing rather than failing.
 */
function MembershipEditor({ account, busy, onSave }: { account: AdminUserDetail; busy: boolean; onSave: (groupIds: string[]) => Promise<void> }) {
  const groups = useRequest<AdminGroupsResponse>((signal) => adminApi.groups(signal), []);
  const current = useMemo(() => account.groups.map((group) => group.id).sort(), [account.groups]);
  const [chosen, setChosen] = useState<string[]>(current);
  useEffect(() => { setChosen(current); }, [current]);
  const dirty = chosen.slice().sort().join(',') !== current.join(',');

  if (account.status !== 'approved') return null;
  if (groups.status === 'loading') {
    return <div className="stack stack--2" aria-busy="true"><Skeleton width="50%" /><Skeleton width="45%" /></div>;
  }
  if (groups.status === 'error' && groups.error) {
    return <FailureView failure={groups.error} what="the groups" onRetry={groups.reload} compact />;
  }
  const rows = groups.data?.items ?? [];
  if (rows.length === 0) {
    return <StateView kind="empty" title="No groups exist yet" description="Create one on the Groups page first." action={<LinkButton to={PATHS.adminGroups} variant="secondary">Go to groups</LinkButton>} compact />;
  }
  const toggle = (groupId: string, checked: boolean): void => {
    setChosen((value) => (checked ? [...new Set([...value, groupId])] : value.filter((entry) => entry !== groupId)));
  };
  return (
    <form
      className="stack"
      onSubmit={(event) => {
        event.preventDefault();
        if (dirty && !busy) void onSave(chosen);
      }}
    >
      <Fieldset legend="Groups">
        {rows.map((group) => {
          const confers = describeConfers(group.confers, groups.data?.actDelegableSections ?? []);
          return (
            <Checkbox
              key={group.id}
              label={group.name}
              description={confers.length > 0 ? confers.join(' · ') : 'Grants nothing yet'}
              checked={chosen.includes(group.id)}
              onChange={(event) => toggle(group.id, event.target.checked)}
              disabled={busy}
            />
          );
        })}
      </Fieldset>
      {dirty && chosen.length === 0 && !account.isSuperuser ? (
        <Banner tone="warning" title="No groups means no access">
          Saving with no group keeps the account approved but removes every right, including access to Calis itself. Suspend or deny instead if that is the intent.
        </Banner>
      ) : null}
      {/* Why (03-components §3.3: a form with unsaved changes SHOULD warn before navigation away; Phase 1c finding G9): the
          groups are saved as one set, and leaving the page dropped an unsaved set without a word. Not while the save is in
          flight: the set has left, and leaving loses nothing. */}
      <UnsavedChangesGuard when={dirty && !busy} what="the changes to this account's groups" />
      <div className="grants-editor__actions">
        <Button type="submit" variant="primary" disabled={!dirty} disabledReason={dirty ? undefined : 'No changes to save.'} loading={busy}>Save groups</Button>
        {dirty ? <Button variant="ghost" onClick={() => setChosen(current)} disabled={busy} disabledReason={busy ? 'Wait for the save to finish.' : undefined}>Discard changes</Button> : <span className="grants-editor__status">No changes to save</span>}
      </div>
    </form>
  );
}

// --- Actions (superuser, not self) ---------------------------------------------------------------

function AccountActions({ account, busy, onApprove, onConfirm }: { account: AdminUserDetail; busy: boolean; onApprove: (mode: 'approve' | 'reinstate') => void; onConfirm: (kind: Confirmation) => void }) {
  const status = account.status;
  // Why the one reason (05-client §5.5): each of these opens a confirmation whose own confirm button shows the write in
  // flight as the loading state; while any write on this page is in flight — a group save, a confirmed action — the
  // actions here are inert and say why, instead of reading as a bug.
  const waiting = busy ? 'Wait for the current change to finish.' : undefined;
  // Why: a superuser's lifecycle belongs to the deployment (the server answers 409
  // superuser_env_managed); only the action that holds is offered (05-client §5.5).
  if (account.isSuperuser) {
    return (
      <div className="account-actions">
        {status === 'approved'
          ? <Button variant="secondary" icon="sign-out" onClick={() => onConfirm('revoke')} disabled={busy} disabledReason={waiting}>Sign out everywhere</Button>
          : <p className="secondary text-sm">No action applies here until superuser:revoke has run.</p>}
      </div>
    );
  }
  return (
    <div className="account-actions">
      {status === 'pending' ? <Button variant="primary" icon="check" onClick={() => onApprove('approve')} disabled={busy} disabledReason={waiting}>Approve</Button> : null}
      {status === 'suspended' || status === 'denied' ? <Button variant="primary" icon="undo" onClick={() => onApprove('reinstate')} disabled={busy} disabledReason={waiting}>Reinstate</Button> : null}
      {status === 'approved' ? <Button variant="secondary" icon="sign-out" onClick={() => onConfirm('revoke')} disabled={busy} disabledReason={waiting}>Sign out everywhere</Button> : null}
      <div className="account-actions__danger">
        {status === 'approved' ? <Button variant="danger-outline" onClick={() => onConfirm('suspend')} disabled={busy} disabledReason={waiting}>Suspend</Button> : null}
        {status === 'pending' || status === 'approved' || status === 'suspended' ? <Button variant="danger-outline" onClick={() => onConfirm('deny')} disabled={busy} disabledReason={waiting}>{status === 'pending' ? 'Refuse' : 'Deny access'}</Button> : null}
        <Button variant="danger" icon="trash" onClick={() => onConfirm('delete')} disabled={busy} disabledReason={waiting}>Delete account</Button>
      </div>
    </div>
  );
}
